import React, { useState, useEffect, Suspense } from 'react';
import { useAB } from '@guardian/ab-react';
import { tests } from '@frontend/web/experiments/ab-tests';
import { EditionDropdown } from '@frontend/web/components/EditionDropdown';
import { ShareCount } from '@frontend/web/components/ShareCount';
import { MostViewedFooter } from '@frontend/web/components/MostViewed/MostViewedFooter/MostViewedFooter';
import { RichLinkComponent } from '@frontend/web/components/elements/RichLinkComponent';
import { CalloutBlockComponent } from '@root/src/web/components/elements/CalloutBlockComponent';
import { YoutubeBlockComponent } from '@root/src/web/components/elements/YoutubeBlockComponent';
import { ReaderRevenueLinks } from '@frontend/web/components/ReaderRevenueLinks';
import { SlotBodyEnd } from '@frontend/web/components/SlotBodyEnd';
import { Links } from '@frontend/web/components/Links';
import { SubNav } from '@frontend/web/components/SubNav/SubNav';
import { GetMatchNav } from '@frontend/web/components/GetMatchNav';
import { Discussion } from '@frontend/web/components/Discussion';
import { StickyBottomBanner } from '@root/src/web/components/StickyBottomBanner/StickyBottomBanner';
import { SignInGateSelector } from '@root/src/web/components/SignInGate/SignInGateSelector';
import { incrementWeeklyArticleCount } from '@guardian/automat-client';
import { QandaAtom, GuideAtom, ProfileAtom, TimelineAtom, ChartAtom, AudioAtom, } from '@guardian/atoms-rendering';
import { Portal } from '@frontend/web/components/Portal';
import { Hydrate } from '@frontend/web/components/Hydrate';
import { Lazy } from '@frontend/web/components/Lazy';
import { Placeholder } from '@root/src/web/components/Placeholder';
import { decidePillar } from '@root/src/web/lib/decidePillar';
import { decideDisplay } from '@root/src/web/lib/decideDisplay';
import { loadScript } from '@root/src/web/lib/loadScript';
import { toTypesPillar } from '@root/src/lib/format';
import { initPerf } from '@root/src/web/browser/initPerf';
import { getCookie } from '@root/src/web/browser/cookie';
import { getCountryCode } from '@frontend/web/lib/getCountryCode';
import { getUser } from '@root/src/web/lib/getUser';
import { getBrazeUuid } from '@root/src/web/lib/getBrazeUuid';
import { FocusStyleManager } from '@guardian/src-foundations/utils';
import { incrementAlreadyVisited } from '@root/src/web/lib/alreadyVisited';
import { incrementDailyArticleCount } from '@frontend/web/lib/dailyArticleCount';
import { getArticleCountConsent } from '@frontend/web/lib/contributions';
import { buildAdTargeting } from '@root/src/lib/ad-targeting';
import { cmp, onConsentChange, getConsentFor, } from '@guardian/consent-management-platform';
import { injectPrivacySettingsLink } from '@root/src/web/lib/injectPrivacySettingsLink';
import { submitComponentEvent, } from '../browser/ophan/ophan';
import { trackPerformance } from '../browser/ga/ga';
// *******************************
// ****** Dynamic imports ********
// *******************************
const MostViewedRightWrapper = React.lazy(() => {
    const { start, end } = initPerf('MostViewedRightWrapper');
    start();
    return import(
    /* webpackChunkName: "MostViewedRightWrapper" */ '@frontend/web/components/MostViewed/MostViewedRight/MostViewedRightWrapper').then((module) => {
        end();
        return { default: module.MostViewedRightWrapper };
    });
});
const OnwardsUpper = React.lazy(() => {
    const { start, end } = initPerf('OnwardsUpper');
    start();
    return import(
    /* webpackChunkName: "OnwardsUpper" */ '@frontend/web/components/Onwards/OnwardsUpper').then((module) => {
        end();
        return { default: module.OnwardsUpper };
    });
});
const OnwardsLower = React.lazy(() => {
    const { start, end } = initPerf('OnwardsLower');
    start();
    return import(
    /* webpackChunkName: "OnwardsLower" */ '@frontend/web/components/Onwards/OnwardsLower').then((module) => {
        end();
        return { default: module.OnwardsLower };
    });
});
const GetMatchStats = React.lazy(() => {
    const { start, end } = initPerf('GetMatchStats');
    start();
    return import(
    /* webpackChunkName: "GetMatchStats" */ '@frontend/web/components/GetMatchStats').then((module) => {
        end();
        return { default: module.GetMatchStats };
    });
});
const componentEventHandler = (componentType, id, action) => () => {
    const componentEvent = {
        component: {
            componentType,
            id,
            products: [],
            labels: [],
        },
        action,
    };
    submitComponentEvent(componentEvent);
};
export const App = ({ CAPI, NAV }) => {
    const [isSignedIn, setIsSignedIn] = useState();
    const [isDigitalSubscriber, setIsDigitalSubscriber] = useState();
    const [user, setUser] = useState();
    const [asyncBrazeUuid, setAsyncBrazeUuid] = useState();
    const [countryCode, setCountryCode] = useState();
    // This is an async version of the countryCode state value defined above.
    // This can be used where you've got logic which depends on countryCode but
    // don't want to block on it becoming available, as you would with the
    // non-async version (this is the case in the banner picker where some
    // banners need countryCode but we don't want to block all banners from
    // executing their canShow logic until countryCode is available):
    const [asyncCountryCode, setAsyncCountryCode] = useState();
    const [shouldUseAcast, setShouldUseAcast] = useState(false);
    const pageViewId = window.guardian?.config?.ophan?.pageViewId;
    // *******************************
    // ** Setup AB Test Tracking *****
    // *******************************
    const ABTestAPI = useAB();
    useEffect(() => {
        const allRunnableTests = ABTestAPI.allRunnableTests(tests);
        ABTestAPI.trackABTests(allRunnableTests);
        ABTestAPI.registerImpressionEvents(allRunnableTests);
        ABTestAPI.registerCompleteEvents(allRunnableTests);
    }, [ABTestAPI]);
    useEffect(() => {
        setIsSignedIn(!!getCookie('GU_U'));
    }, []);
    useEffect(() => {
        setIsDigitalSubscriber(getCookie('gu_digital_subscriber') === 'true');
    }, []);
    useEffect(() => {
        const callGetUser = async () => {
            setUser(await getUser(CAPI.config.discussionApiUrl));
        };
        if (isSignedIn) {
            callGetUser();
        }
    }, [isSignedIn, CAPI.config.discussionApiUrl]);
    useEffect(() => {
        // Don't do anything until isSignedIn is defined as we only want to set
        // asyncBrazeUuid once
        if (isSignedIn === undefined) {
            return;
        }
        if (isSignedIn) {
            setAsyncBrazeUuid(getBrazeUuid(CAPI.config.idApiUrl));
        }
        else {
            setAsyncBrazeUuid(Promise.resolve(null));
        }
    }, [isSignedIn, CAPI.config.idApiUrl]);
    useEffect(() => {
        const callFetch = () => {
            const countryCodePromise = getCountryCode();
            setAsyncCountryCode(countryCodePromise);
            countryCodePromise.then((cc) => setCountryCode(cc || ''));
        };
        callFetch();
    }, []);
    useEffect(() => {
        incrementAlreadyVisited();
    }, []);
    // Log an article view using the Slot Machine client lib
    // This function must be called once per article serving.
    // We should monitor this function call to ensure it only happens within an
    // article pages when other pages are supported by DCR.
    useEffect(() => {
        const incrementArticleCountsIfConsented = async () => {
            if (await getArticleCountConsent()) {
                incrementDailyArticleCount();
                incrementWeeklyArticleCount();
            }
        };
        incrementArticleCountsIfConsented();
    }, []);
    // Ensure the focus state of any buttons/inputs in any of the Source
    // components are only applied when navigating via keyboard.
    // READ: https://www.theguardian.design/2a1e5182b/p/6691bb-accessibility/t/32e9fb
    useEffect(() => {
        FocusStyleManager.onlyShowFocusOnTabs();
    }, []);
    useEffect(() => {
        // Used internally only, so only import each function on demand
        const loadAndRun = (key) => (asExistingSupporter) => import(
        /* webpackChunkName: "readerRevenueDevUtils" */ '@frontend/web/lib/readerRevenueDevUtils')
            .then((utils) => utils[key](asExistingSupporter, CAPI.shouldHideReaderRevenue))
            /* eslint-disable no-console */
            .catch((error) => console.log('Error loading readerRevenueDevUtils', error));
        /* eslint-enable no-console */
        if (window && window.guardian) {
            window.guardian.readerRevenue = {
                changeGeolocation: loadAndRun('changeGeolocation'),
                showMeTheEpic: loadAndRun('showMeTheEpic'),
                showMeTheBanner: loadAndRun('showMeTheBanner'),
                showNextVariant: loadAndRun('showNextVariant'),
                showPreviousVariant: loadAndRun('showPreviousVariant'),
            };
        }
    }, [CAPI.shouldHideReaderRevenue]);
    // kick off the CMP...
    useEffect(() => {
        // the UI is injected automatically into the page,
        // and is not a react component, so it's
        // handled in here.
        if (CAPI.config.switches.consentManagement && countryCode) {
            const pubData = {
                browserId: getCookie('bwid') || undefined,
                pageViewId,
            };
            injectPrivacySettingsLink(); // manually updates the footer DOM because it's not hydrated
            // keep this in sync with CONSENT_TIMING in static/src/javascripts/boot.js in frontend
            // mark: CONSENT_TIMING
            let recordedConsentTime = false;
            onConsentChange(() => {
                if (!recordedConsentTime) {
                    recordedConsentTime = true;
                    cmp.willShowPrivacyMessage().then((willShow) => {
                        trackPerformance('consent', 'acquired', willShow ? 'new' : 'existing');
                    });
                }
            });
            cmp.init({
                country: countryCode,
                pubData,
            });
        }
    }, [countryCode, CAPI.config.switches.consentManagement]);
    // *****************
    // *     ACast     *
    // *****************
    useEffect(() => {
        onConsentChange((state) => {
            // Should we use ad enabled audio? If so, then set the shouldUseAcast
            // state to true, triggering a rerender of AudioAtom using a new track url
            // (one with adverts)
            const consentGiven = getConsentFor('acast', state);
            const aCastisEnabled = CAPI.config.switches.acast;
            const readerCanBeShownAds = !CAPI.isAdFreeUser;
            const contentIsNotSensitive = !CAPI.config.isSensitive;
            if (aCastisEnabled &&
                consentGiven &&
                readerCanBeShownAds && // Eg. Not a subscriber
                contentIsNotSensitive) {
                setShouldUseAcast(true);
            }
        });
    }, [
        CAPI.config.switches.acast,
        CAPI.isAdFreeUser,
        CAPI.config.isSensitive,
    ]);
    // ************************
    // *   Google Analytics   *
    // ************************
    useEffect(() => {
        onConsentChange((state) => {
            const consentGiven = getConsentFor('google-analytics', state);
            if (consentGiven) {
                loadScript('https://www.google-analytics.com/analytics.js');
                loadScript(window.guardian.gaPath);
            }
            else {
                window.ga = null;
            }
        });
    }, []);
    const pillar = decidePillar(CAPI);
    const display = decideDisplay(CAPI);
    const adTargeting = buildAdTargeting(CAPI.config);
    return (
    // Do you need to Hydrate or do you want a Portal?
    //
    // Hydrate: If your component is server rendered and you're hydrating it with
    // more data or making it interactive on the client and you do not need to access
    // global application state.
    //
    // Portal: If your component is not server rendered but a pure client-side component
    // and/or you want to access global application state, you want to use a Portal.
    //
    // Note: Both require a 'root' element that needs to be server rendered.
    React.createElement(React.StrictMode, null,
        React.createElement(Portal, { root: "reader-revenue-links-header" },
            React.createElement(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.header, edition: CAPI.editionId, dataLinkNamePrefix: "nav2 : ", inHeader: true, pageViewId: pageViewId })),
        React.createElement(Hydrate, { root: "links-root" },
            React.createElement(Links, { userId: user ? user.userId : undefined })),
        React.createElement(Hydrate, { root: "edition-root" },
            React.createElement(EditionDropdown, { edition: CAPI.editionId, dataLinkName: "nav2 : topbar : edition-picker: toggle" })),
        React.createElement(Portal, { root: "share-count-root" },
            React.createElement(ShareCount, { ajaxUrl: CAPI.config.ajaxUrl, pageId: CAPI.pageId })),
        CAPI.youtubeMainMediaBlockElement.map((youtubeBlock, index) => (React.createElement(Hydrate, { key: index, root: "youtube-block-main-media", index: youtubeBlock.youtubeIndex },
            React.createElement(YoutubeBlockComponent, { display: display, designType: CAPI.designType, element: youtubeBlock, pillar: pillar, hideCaption: false, 
                // eslint-disable-next-line jsx-a11y/aria-role
                role: "inline", adTargeting: adTargeting, isMainMedia: false, overlayImage: youtubeBlock.overrideImage, duration: youtubeBlock.duration, origin: CAPI.config.host })))),
        CAPI.youtubeBlockElement.map((youtubeBlock, index) => (React.createElement(Hydrate, { key: index, root: "youtube-block", index: youtubeBlock.youtubeIndex },
            React.createElement(YoutubeBlockComponent, { display: display, designType: CAPI.designType, element: youtubeBlock, pillar: pillar, hideCaption: false, 
                // eslint-disable-next-line jsx-a11y/aria-role
                role: "inline", adTargeting: adTargeting, isMainMedia: false, overlayImage: youtubeBlock.overrideImage, duration: youtubeBlock.duration, origin: CAPI.config.host })))),
        NAV.subNavSections && (React.createElement(Hydrate, { root: "sub-nav-root" },
            React.createElement(React.Fragment, null,
                React.createElement(SubNav, { subNavSections: NAV.subNavSections, currentNavLink: NAV.currentNavLink, pillar: pillar })))),
        CAPI.matchUrl && (React.createElement(Portal, { root: "match-nav" },
            React.createElement(GetMatchNav, { matchUrl: CAPI.matchUrl }))),
        CAPI.richLinks.map((link, index) => (React.createElement(Portal, { key: index, root: "rich-link", richLinkIndex: link.richLinkIndex },
            React.createElement(RichLinkComponent, { element: link, pillar: pillar, ajaxEndpoint: CAPI.config.ajaxUrl, richLinkIndex: index })))),
        CAPI.callouts.map((callout) => (React.createElement(Hydrate, { root: "callout", index: callout.calloutIndex },
            React.createElement(CalloutBlockComponent, { callout: callout, pillar: pillar })))),
        CAPI.chartAtoms.map((chart) => (React.createElement(Hydrate, { root: "chart-atom", index: chart.chartIndex },
            React.createElement(ChartAtom, { id: chart.id, html: chart.html })))),
        CAPI.audioAtoms.map((audioAtom) => (React.createElement(Hydrate, { root: "audio-atom", index: audioAtom.audioIndex },
            React.createElement(AudioAtom, { id: audioAtom.id, trackUrl: audioAtom.trackUrl, kicker: audioAtom.kicker, title: audioAtom.title, pillar: toTypesPillar(pillar), shouldUseAcast: shouldUseAcast })))),
        CAPI.qandaAtoms.map((qandaAtom) => (React.createElement(Hydrate, { root: "qanda-atom", index: qandaAtom.qandaIndex },
            React.createElement(QandaAtom, { id: qandaAtom.id, title: qandaAtom.title, html: qandaAtom.html, image: qandaAtom.img, credit: qandaAtom.credit, pillar: pillar, likeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'EXPAND') })))),
        CAPI.guideAtoms.map((guideAtom) => (React.createElement(Hydrate, { root: "guide-atom", index: guideAtom.guideIndex },
            React.createElement(GuideAtom, { id: guideAtom.id, title: guideAtom.title, html: guideAtom.html, image: guideAtom.img, credit: guideAtom.credit, pillar: pillar, likeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'EXPAND') })))),
        CAPI.profileAtoms.map((profileAtom) => (React.createElement(Hydrate, { root: "profile-atom", index: profileAtom.profileIndex },
            React.createElement(ProfileAtom, { id: profileAtom.id, title: profileAtom.title, html: profileAtom.html, image: profileAtom.img, credit: profileAtom.credit, pillar: pillar, likeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'EXPAND') })))),
        CAPI.timelineAtoms.map((timelineAtom) => (React.createElement(Hydrate, { root: "timeline-atom", index: timelineAtom.timelineIndex },
            React.createElement(TimelineAtom, { id: timelineAtom.id, title: timelineAtom.title, events: timelineAtom.events, description: timelineAtom.description, pillar: pillar, likeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'EXPAND') })))),
        React.createElement(Portal, { root: "most-viewed-right" },
            React.createElement(Lazy, { margin: 100 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(MostViewedRightWrapper, { pillar: pillar })))),
        CAPI.matchUrl && (React.createElement(Portal, { root: "match-stats" },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(Placeholder, { height: 800 }) },
                    React.createElement(GetMatchStats, { matchUrl: CAPI.matchUrl }))))),
        React.createElement(Portal, { root: "slot-body-end" },
            React.createElement(SlotBodyEnd, { isSignedIn: isSignedIn, countryCode: countryCode, contentType: CAPI.contentType, sectionName: CAPI.sectionName, shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue, isMinuteArticle: CAPI.pageType.isMinuteArticle, isPaidContent: CAPI.pageType.isPaidContent, isSensitive: CAPI.config.isSensitive, tags: CAPI.tags, contributionsServiceUrl: CAPI.contributionsServiceUrl })),
        React.createElement(Portal, { root: isSignedIn
                ? 'onwards-upper-whensignedin'
                : 'onwards-upper-whensignedout' },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(OnwardsUpper, { ajaxUrl: CAPI.config.ajaxUrl, hasRelated: CAPI.hasRelated, hasStoryPackage: CAPI.hasStoryPackage, isAdFreeUser: CAPI.isAdFreeUser, pageId: CAPI.pageId, isPaidContent: CAPI.config.isPaidContent || false, showRelatedContent: CAPI.config.showRelatedContent, keywordIds: CAPI.config.keywordIds, contentType: CAPI.contentType, tags: CAPI.tags, edition: CAPI.editionId, pillar: pillar })))),
        React.createElement(Portal, { root: isSignedIn
                ? 'onwards-lower-whensignedin'
                : 'onwards-lower-whensignedout' },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(OnwardsLower, { ajaxUrl: CAPI.config.ajaxUrl, hasStoryPackage: CAPI.hasStoryPackage, tags: CAPI.tags, pillar: pillar })))),
        React.createElement(Portal, { root: "sign-in-gate" },
            React.createElement(SignInGateSelector, { isSignedIn: isSignedIn, CAPI: CAPI })),
        React.createElement(Hydrate, { root: "comments" },
            React.createElement(Discussion, { discussionApiUrl: CAPI.config.discussionApiUrl, shortUrlId: CAPI.config.shortUrlId, isCommentable: CAPI.isCommentable, pillar: pillar, user: user, discussionD2Uid: CAPI.config.discussionD2Uid, discussionApiClientHeader: CAPI.config.discussionApiClientHeader, enableDiscussionSwitch: CAPI.config.enableDiscussionSwitch, isAdFreeUser: CAPI.isAdFreeUser, shouldHideAds: CAPI.shouldHideAds, beingHydrated: true })),
        React.createElement(Portal, { root: "most-viewed-footer" },
            React.createElement(MostViewedFooter, { pillar: pillar, sectionName: CAPI.sectionName, ajaxUrl: CAPI.config.ajaxUrl })),
        React.createElement(Portal, { root: "reader-revenue-links-footer" },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.footer, edition: CAPI.editionId, dataLinkNamePrefix: "footer : ", inHeader: false, pageViewId: pageViewId }))),
        React.createElement(Portal, { root: "bottom-banner" },
            React.createElement(StickyBottomBanner, { isSignedIn: isSignedIn, asyncCountryCode: asyncCountryCode, CAPI: CAPI, asyncBrazeUuid: asyncBrazeUuid, isDigitalSubscriber: isDigitalSubscriber }))));
};
